/**
 * ----------------------------------------------------------------------------
 * List Chart Component
 * ----------------------------------------------------------------------------
 * Summary:
 * Component for displaying nonconformity records in a tabular list view.
 * Supports localized column headers, date formatting, responsive table layout,
 * and an empty-state message when no nonconformity records are available.
 *
 * @author Chandan Singh
 * @created September-2026
 * ----------------------------------------------------------------------------
 */
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import noMessageIcon from '../../../assets/Images/no-noconfirmitry.png';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useStore } from '../../../contexts/store-provider';
import Grid from '../../../shared-components/Grid/Grid';
import { GridRemoteConstant } from '../../../constants/grid-constant';
import IDefaultColumSortOrder from '../../../models/IDefaultColumSortOrder';
import { formatMessage } from '../../../translations/format-message';

function ListChart(props: any) {
  const { nonconformityStore } = useStore();
  const { GetNonConformanceReportService } = nonconformityStore;
  const { fromDate, toDate, pageInfo, pageLimitInfo } = props;
  const { listViewList } = props;

  const [columSortOrder, setColumnSortOrder] = useState<IDefaultColumSortOrder>({
    dataField: 'DetectionDate',
    order: 'desc',
  });

  useEffect(() => {
    GetNonConformanceReportService(
      fromDate,
      toDate,
      pageInfo?.currentPage,
      pageLimitInfo?.pageLimit,
      columSortOrder.dataField,
      columSortOrder.order
    );
  }, [pageInfo?.currentPage, pageLimitInfo?.pageLimit, fromDate, toDate]);

  const handleTableChange = (type: any, newState: any) => {
    if (type === 'sort' && newState.sortField) {
      setColumnSortOrder({
        dataField: newState.sortField,
        order: newState.sortOrder,
      });
      GetNonConformanceReportService(
        fromDate,
        toDate,
        pageInfo?.currentPage,
        pageLimitInfo?.pageLimit,
        newState.sortField,
        newState.sortOrder
      );
    }
  };

  const columns: any = [
    {
      dataField: 'TrayId',
      text: formatMessage('tray_id'),
      classes: 'show-Elipses',
      title: (cell: string) => (cell?.length > 10 ? cell : undefined),
      sort: true,
    },
    {
      dataField: 'DetectionDate',
      text: formatMessage('detection_date'),
      classes: 'show-Elipses',
      title: (cell: string) => {
        return cell.length > 10 ? cell : undefined;
      },
      sort: true,
    },
    {
      dataField: 'DetectionProcess',
      text: formatMessage('detection_process'),
      classes: 'show-Elipses',
      title: (cell: string) => (cell?.length > 10 ? cell : undefined),
      sort: true,
    },
    {
      dataField: 'Detector',
      text: formatMessage('detector'),
      classes: 'show-Elipses',
      title: (cell: string) => (cell?.length > 10 ? cell : undefined),
      sort: true,
    },
    {
      dataField: 'WorkingDays',
      text: formatMessage('working_days'),
      classes: 'show-Elipses',
      title: (cell: string) => {
        return cell.length > 10 ? cell : undefined;
      },
      sort: false,
    },
    {
      dataField: 'ReturnProcessName',
      text: formatMessage('work_process'),
      classes: 'show-Elipses',
      title: (cell: string) => (cell?.length > 10 ? cell : undefined),
      sort: true,
    },
    {
      dataField: 'SubContractor',
      text: formatMessage('sub_contractor_name'),
      classes: 'show-Elipses',
      title: (cell: string) => (cell?.length > 10 ? cell : undefined),
      sort: true,
    },
    {
      dataField: 'Worker',
      text: formatMessage('worker'),
      classes: 'show-Elipses',
      title: (cell: string) => (cell?.length > 10 ? cell : undefined),
      sort: true,
    },
    {
      dataField: 'Katashiki',
      text: formatMessage('katashiki'),
      classes: 'show-Elipses',
      title: (cell: string) => (cell?.length > 10 ? cell : undefined),
      sort: true,
    },
    {
      dataField: 'Kishu',
      text: formatMessage('kishu'),
      classes: 'show-Elipses',
      title: (cell: string) => (cell?.length > 10 ? cell : undefined),
      sort: true,
    },
    {
      dataField: 'NoOfDefectiveItems',
      text: formatMessage('number_of_defective_items'),
      classes: 'nc-col-num show-Elipses',
      formatter: (cell: any) => cell ?? 0,
      sort: false,
    },
    {
      dataField: 'CorrectionTime',
      text: formatMessage('correction_time'),
      classes: 'nc-col-num show-Elipses',
      formatter: (cell: any) => cell ?? '-',
      sort: false,
    },
  ];

  return (
    <>
      {!listViewList || !listViewList.length ? (
        <div className="message">
          <div className="d-flex message-area">
            <div className="no-data-img">
              <img src={noMessageIcon} alt="" className="no_message" />
            </div>
            <div>
              <h3 className="fw-bold display-6">
                <FormattedMessage id="no_nonconformity" />
              </h3>
            </div>
          </div>
        </div>
      ) : (
        <div className="tableDesign row-eq-height tableDevices p-3">
          <div className="table-responsive nc-table-wrap" data-testid="table">
            <Grid
              keyField="Id"
              data={listViewList}
              columns={columns}
              hover={true}
              sort={{ ...columSortOrder }}
              remote={{ ...GridRemoteConstant }}
              onTableChange={handleTableChange}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default observer(ListChart);